import { inviteMemberSchema, MEMBER_ROLES, type MemberRole } from '@alicerce/contracts';
import { Alert, Badge, Button, Card, CardHeader, Dialog, Field, formatDate, PageHeader, PageLoader, Select, Table, Td, TextField, Th, Tr } from '@alicerce/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { errorMessage } from '@/lib/api';
import { useZodForm } from '@/lib/form';
import { useApi, useCurrent, useSession } from '@/lib/session';

export const ROLE_LABEL: Record<MemberRole, string> = {
  OWNER: 'Dono',
  ADMIN: 'Administrador',
  SELLER: 'Vendedor',
  FINANCE: 'Financeiro',
  AGENT: 'Atendente',
};

const ROLE_HINT: Record<MemberRole, string> = {
  OWNER: 'Tudo, inclusive assinatura e conta de recebimento.',
  ADMIN: 'Tudo, menos assinatura e dados bancários.',
  SELLER: 'Pedidos, clientes, venda rápida e atendimento.',
  FINANCE: 'Financeiro e notas fiscais. Bom para o contador.',
  AGENT: 'Só o atendimento.',
};

interface TeamResponse {
  members: { id: string; role: MemberRole; user: { id: string; name: string; email: string } | null; since: string }[];
  invites: { id: string; email: string; role: MemberRole; expiresAt: string; createdAt: string }[];
}

export function TeamPage() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { me } = useSession();
  const { can } = useCurrent();
  const manage = can('team:manage');
  const [inviting, setInviting] = useState(false);
  const q = useQuery({ queryKey: ['team'], queryFn: () => api.get<TeamResponse>('/v1/core/members') });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['team'] });
  const changeRole = useMutation({ mutationFn: (v: { id: string; role: MemberRole }) => api.patch(`/v1/core/members/${v.id}`, { role: v.role }), onSuccess: refresh });
  const removeMember = useMutation({ mutationFn: (id: string) => api.delete(`/v1/core/members/${id}`), onSuccess: refresh });
  const revoke = useMutation({ mutationFn: (id: string) => api.delete(`/v1/core/members/invites/${id}`), onSuccess: refresh });
  const error = changeRole.error ?? removeMember.error ?? revoke.error;

  return (
    <div className="space-y-6">
      <PageHeader title="Equipe" description="Quem acessa o painel e o que cada um pode fazer." actions={manage && <Button onClick={() => setInviting(true)}>Convidar pessoa</Button>} />
      {error && <Alert tone="danger">{errorMessage(error)}</Alert>}
      {q.isPending ? (
        <PageLoader />
      ) : q.isError ? (
        <Alert tone="danger">{errorMessage(q.error)}</Alert>
      ) : (
        <>
          <Card>
            <CardHeader title="Membros" />
            <Table>
              <thead>
                <tr>
                  <Th>Pessoa</Th>
                  <Th>Papel</Th>
                  <Th>Desde</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {q.data.members.map((m) => {
                  const self = m.user?.id === me?.user.id;
                  return (
                    <Tr key={m.id}>
                      <Td>
                        <p className="font-medium">
                          {m.user?.name ?? '—'} {self && <Badge>você</Badge>}
                        </p>
                        <p className="text-xs text-muted">{m.user?.email}</p>
                      </Td>
                      <Td>
                        {manage && !self && m.role !== 'OWNER' ? (
                          <Select aria-label={`Papel de ${m.user?.name ?? 'membro'}`} className="w-44" value={m.role} onChange={(e) => changeRole.mutate({ id: m.id, role: e.target.value as MemberRole })}>
                            {MEMBER_ROLES.filter((r) => r !== 'OWNER').map((r) => (
                              <option key={r} value={r}>
                                {ROLE_LABEL[r]}
                              </option>
                            ))}
                          </Select>
                        ) : (
                          ROLE_LABEL[m.role]
                        )}
                      </Td>
                      <Td>{formatDate(m.since)}</Td>
                      <Td className="text-right">
                        {manage && !self && m.role !== 'OWNER' && (
                          <Button size="sm" variant="ghost" onClick={() => removeMember.mutate(m.id)}>
                            Remover
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </Card>
          {q.data.invites.length > 0 && (
            <Card>
              <CardHeader title="Convites pendentes" description="O link do convite vale 7 dias." />
              <Table>
                <tbody>
                  {q.data.invites.map((i) => (
                    <Tr key={i.id}>
                      <Td>{i.email}</Td>
                      <Td>{ROLE_LABEL[i.role]}</Td>
                      <Td>vence em {formatDate(i.expiresAt)}</Td>
                      <Td className="text-right">
                        {manage && (
                          <Button size="sm" variant="ghost" onClick={() => revoke.mutate(i.id)}>
                            Cancelar convite
                          </Button>
                        )}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          )}
        </>
      )}
      {inviting && <InviteDialog onClose={() => setInviting(false)} onDone={refresh} />}
    </div>
  );
}

function InviteDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const api = useApi();
  const form = useZodForm(inviteMemberSchema, { email: '', role: 'SELLER' as MemberRole });
  const onSubmit = form.submit(async (data) => {
    await api.post('/v1/core/members/invite', data);
    onDone();
    onClose();
  });
  return (
    <Dialog
      open
      onClose={onClose}
      title="Convidar pessoa"
      description="Enviamos um e-mail com o link para entrar na equipe."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button loading={form.submitting} onClick={() => void onSubmit()}>
            Enviar convite
          </Button>
        </>
      }
    >
      <form className="space-y-3" onSubmit={onSubmit} noValidate>
        {form.formError && <Alert tone="danger">{form.formError}</Alert>}
        <TextField label="E-mail" type="email" value={form.values.email} error={form.errors.email} onChange={(e) => form.set('email', e.target.value)} />
        <Field label="Papel" hint={ROLE_HINT[form.values.role]} error={form.errors.role}>
          {(a) => (
            <Select {...a} value={form.values.role} onChange={(e) => form.set('role', e.target.value as MemberRole)}>
              {MEMBER_ROLES.filter((r) => r !== 'OWNER').map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABEL[r]}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <button type="submit" hidden />
      </form>
    </Dialog>
  );
}
